import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Form, Input, Spin, Switch, Typography, message } from "antd";
import { ArrowLeft, Save } from "lucide-react";
import styled from "@emotion/styled";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useProductTypeApi, { ProductTypeUpdateRequest } from "apiClients/useProductTypeApiClient";
import { useLocaleStore } from "stores/localeStore";
import ProductTypeTreeSelect from "./ProductTypeTreeSelect";
import { PRODUCT_TYPES_QUERY_KEY } from "../hooks/useProductTypesQuery";

const StyledCard = styled(Card)`
  max-width: 800px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const TranslationSection = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const ProductTypeEditView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { getProductType, updateProductType } = useProductTypeApi();
  const { languages } = useLocaleStore();
  const queryClient = useQueryClient();

  const { data: productType, isLoading, error } = useQuery({
    queryKey: ["product-type", id],
    queryFn: () => getProductType(Number(id)),
    enabled: !!id && !isNaN(Number(id)),
  });

  useEffect(() => {
    if (!productType) return;

    const translationFields: Record<string, string> = {};
    productType.translations?.forEach((translation) => {
      if (translation.languageId) {
        translationFields[`name_${translation.languageId}`] = translation.name;
      }
    });

    form.setFieldsValue({
      parentProductTypeId: productType.parentProductTypeId ?? null,
      isActive: productType.isActive !== false,
      ...translationFields,
    });
  }, [productType, form]);

  const handleSubmit = async (values: Record<string, any>) => {
    if (!id) return;

    setSaving(true);
    try {
      const translations = languages
        .map((language) => ({
          languageId: language.id,
          name: (values[`name_${language.id}`] as string | undefined)?.trim() || "",
        }))
        .filter((t) => t.name.length > 0);

      if (translations.length === 0) {
        message.error("Minst en översättning krävs");
        return;
      }

      const updateRequest: ProductTypeUpdateRequest = {
        parentProductTypeId: values.parentProductTypeId ?? null,
        isActive: values.isActive !== false,
        translations,
      };

      await updateProductType(Number(id), updateRequest);
      message.success("Produkttyp uppdaterad");
      queryClient.invalidateQueries({ queryKey: [PRODUCT_TYPES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["product-type", id] });
    } catch (e) {
      message.error("Fel vid uppdatering av produkttyp");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !productType) {
    return (
      <StyledCard>
        <Typography.Text type="danger">Kunde inte ladda produkttypen.</Typography.Text>
        <Button style={{ marginTop: "1rem" }} onClick={() => navigate("/productTypes")}>
          Tillbaka
        </Button>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <HeaderSection>
        <Button icon={<ArrowLeft size={16} />} onClick={() => navigate("/productTypes")}>
          Tillbaka
        </Button>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Redigera produkttyp
        </Typography.Title>
      </HeaderSection>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item label="Aktiv" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label="Överordnad produkttyp" name="parentProductTypeId">
          <ProductTypeTreeSelect
            excludeId={productType.id}
            allowClear
            placeholder="Ingen (rotnivå)"
          />
        </Form.Item>

        <Typography.Title level={5}>Översättningar</Typography.Title>
        {languages.map((language) => (
          <TranslationSection key={language.id}>
            <Form.Item
              label={`Namn (${language.locale})`}
              name={`name_${language.id}`}
            >
              <Input maxLength={1024} />
            </Form.Item>
          </TranslationSection>
        ))}

        <ButtonGroup>
          <Button onClick={() => navigate("/productTypes")}>Avbryt</Button>
          <Button type="primary" htmlType="submit" loading={saving} icon={<Save size={16} />}>
            Spara
          </Button>
        </ButtonGroup>
      </Form>
    </StyledCard>
  );
};

export default ProductTypeEditView;
